// =====================================================================
// tpCustomer — Library
//
// Tabbed sales-enablement document library backed by the /apicrm/library
// endpoints. Tiles render title, type, description, and a download icon.
// Editors / admins see an upload button that triggers an upsert keyed on
// (category, filename) — re-uploading the same filename replaces the
// existing entry.
// =====================================================================

const LIBRARY_CATEGORIES = ["Generic", "Champion", "Influencer", "ICPs", "Internal", "Pilots", "LeadGen"];

const LIBRARY_CATEGORY_BLURB = {
  Generic:    "Pitch decks, product collateral and battle cards usable across any deal.",
  Champion:   "Materials to enable and arm an internal Champion to sell on your behalf.",
  Influencer: "Content for influencers who shape the buying committee without owning the budget.",
  ICPs:       "Persona-specific decks, one-pagers and outreach playbooks for the four ICPs.",
  Internal:   "Onboarding, training and process docs that stay inside the GTM team.",
  Pilots:     "Pilot-engagement assets: agreements, baseline templates, gate decks, close-out and case-study templates.",
  LeadGen:    "Lead-generation assets: outbound sequences, list-building playbooks, cold-email and prospecting templates.",
};

// Map ext → tint used on the type badge. Keep the palette small so
// glance-readable without becoming noisy.
const TYPE_TONE = {
  pptx: "amber", ppt: "amber",
  docx: "blue",  doc: "blue",
  xlsx: "green", xls: "green", csv: "green",
  pdf:  "red",
  png:  "slate", jpg: "slate", jpeg: "slate", gif: "slate",
  mp4:  "slate", mov: "slate", webm: "slate",
  zip:  "slate",
};

// Types the browser can render in a tab. Mirrors INLINE_TYPES in
// tpCustBe/controllers/libraryController.js — keep the two in sync.
const VIEWABLE_EXTS = new Set(["pdf", "png", "jpg", "jpeg", "gif", "webp", "html"]);

function formatBytes(n) {
  if (!n || n < 0) return "—";
  if (n < 1024) return n + " B";
  if (n < 1024 * 1024) return (n / 1024).toFixed(1) + " KB";
  return (n / (1024 * 1024)).toFixed(1) + " MB";
}

function LibraryTile({ doc, canEdit, onDelete }) {
  const tone = TYPE_TONE[(doc.ext || "").toLowerCase()] || "slate";
  const viewable = VIEWABLE_EXTS.has((doc.ext || "").toLowerCase());
  return (
    <div className="card" style={{ display: "flex", flexDirection: "column", gap: 8, padding: 16, minHeight: 180 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 14, lineHeight: 1.3, wordBreak: "break-word" }}>{doc.title}</div>
          <div className="muted" style={{ fontSize: 11.5, marginTop: 2, wordBreak: "break-all" }}>{doc.filename}</div>
        </div>
        <Badge tone={tone}>{doc.typeLabel || (doc.ext ? doc.ext.toUpperCase() : "File")}</Badge>
      </div>
      <div className="muted" style={{ fontSize: 12.5, lineHeight: 1.45, flex: 1, display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
        {doc.description || <em>No description.</em>}
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginTop: "auto", fontSize: 11.5 }}>
        <span className="muted">{formatBytes(doc.size)} · {fmtDateShort(doc.uploadedAt)}</span>
        <div style={{ display: "flex", gap: 6 }}>
          {canEdit && (
            <button
              className="btn btn-sm"
              title="Delete document"
              onClick={() => onDelete(doc)}
              style={{ padding: "2px 8px", color: "var(--red)" }}
            >🗑</button>
          )}
          {viewable && (
            <a
              className="btn btn-sm"
              title="Open in a new tab"
              href={tpAPI.library.viewUrl(doc.id)}
              target="_blank"
              rel="noopener noreferrer"
              style={{ padding: "2px 10px", display: "inline-flex", alignItems: "center", gap: 4 }}
            >
              <span aria-hidden="true">👁</span><span>View</span>
            </a>
          )}
          <a
            className="btn btn-primary btn-sm"
            title="Download"
            href={tpAPI.library.fileUrl(doc.id)}
            // Browser's same-origin Set-Cookie carries the JWT; no token
            // parameter is needed on the URL.
            download={doc.filename}
            style={{ padding: "2px 10px", display: "inline-flex", alignItems: "center", gap: 4 }}
          >
            <span aria-hidden="true">⬇</span><span>Download</span>
          </a>
        </div>
      </div>
    </div>
  );
}

function UploadLibraryDialog({ category, onClose, onUploaded }) {
  const [file, setFile]               = useState(null);
  const [title, setTitle]             = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState(null);

  function handleFile(e) {
    const f = e.target.files && e.target.files[0];
    if (!f) { setFile(null); return; }
    setFile(f);
    if (!title) setTitle(f.name.replace(/\.[A-Za-z0-9]+$/, ""));
  }

  async function handleSave() {
    if (!file)         { setError("Pick a file.");  return; }
    if (!title.trim()) { setError("Title is required."); return; }
    setSaving(true); setError(null);
    try {
      const row = await tpAPI.library.upload(category, file, title.trim(), description.trim());
      onUploaded(row);
      onClose();
    } catch (e) {
      setError(e.message || "Upload failed.");
      setSaving(false);
    }
  }

  return (
    <Dialog
      title={`Upload to ${category}`}
      onClose={onClose}
      footer={<>
        <button className="btn btn-sm" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving || !file || !title.trim()}>
          {saving ? "Uploading…" : "Upload"}
        </button>
      </>}
    >
      <p className="muted" style={{ margin: 0, fontSize: 13 }}>Re-uploading a file with the same name replaces the existing entry.</p>
      <div className="form-field">
        <label>File *</label>
        <input type="file" onChange={handleFile} />
        {file && <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>{file.name} · {formatBytes(file.size)}</div>}
      </div>
      <div className="form-field">
        <label>Title *</label>
        <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Champion advocacy deck" />
      </div>
      <div className="form-field">
        <label>Description</label>
        <textarea
          rows={4}
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="What the document is for, when to use it, who it's aimed at…"
        />
      </div>
      {error && <div className="form-error">{error}</div>}
    </Dialog>
  );
}

function LibraryPage({ navigate }) {
  const [tab, setTab]         = useState(LIBRARY_CATEGORIES[0]);
  const [docs, setDocs]       = useState([]);
  const [status, setStatus]   = useState("loading");
  const [error, setError]     = useState(null);
  const [showUpload, setShow] = useState(false);
  const [confirmDel, setDel]  = useState(null);
  const auth = useAuth();
  const role = (auth && auth.user && auth.user.role) || "viewer";
  const canEdit = role === "admin" || role === "editor";

  async function reload(category) {
    setStatus("loading");
    try {
      const rows = await tpAPI.library.list(category);
      setDocs(rows || []);
      setStatus("ready");
    } catch (e) {
      setError(e.message || "Failed to load library");
      setStatus("error");
    }
  }
  useEffect(() => { reload(tab); }, [tab]);

  async function handleDelete(doc) {
    try {
      await tpAPI.library.remove(doc.id);
      setDel(null);
      reload(tab);
    } catch (e) { setError(e.message || "Delete failed"); }
  }

  const tabs = LIBRARY_CATEGORIES.map(c => ({ key: c, label: c }));

  return (
    <>
      <PageHead
        title="Library"
        sub={LIBRARY_CATEGORY_BLURB[tab]}
        actions={canEdit
          ? <button className="btn btn-primary btn-sm" onClick={() => setShow(true)}>+ Upload to {tab}</button>
          : null}
      />
      <Tabs tabs={tabs} current={tab} onChange={setTab} />

      {status === "loading" && <div className="card"><div className="card-body muted">Loading…</div></div>}
      {status === "error"   && <div className="card"><div className="card-body" style={{ color: "var(--red)" }}>{error}</div></div>}
      {status === "ready" && docs.length === 0 && (
        <div className="card"><div className="card-body muted">
          No documents in <strong>{tab}</strong> yet.
          {canEdit ? <> Click <strong>+ Upload to {tab}</strong> to add the first one.</> : null}
        </div></div>
      )}
      {status === "ready" && docs.length > 0 && (
        <div className="grid grid-3" style={{ gap: 12 }}>
          {docs.map(d => (
            <LibraryTile key={d.id} doc={d} canEdit={canEdit} onDelete={setDel} />
          ))}
        </div>
      )}

      {showUpload && (
        <UploadLibraryDialog
          category={tab}
          onClose={() => setShow(false)}
          onUploaded={() => reload(tab)}
        />
      )}
      {confirmDel && (
        <Dialog
          title="Delete document"
          onClose={() => setDel(null)}
          footer={<>
            <button className="btn btn-sm" onClick={() => setDel(null)}>Cancel</button>
            <button className="btn btn-sm" style={{ background: "var(--red)", color: "#fff", borderColor: "var(--red)" }} onClick={() => handleDelete(confirmDel)}>Delete</button>
          </>}
        >
          <p style={{ margin: 0 }}>Delete <strong>{confirmDel.title}</strong> ({confirmDel.filename})? This cannot be undone.</p>
        </Dialog>
      )}
    </>
  );
}

window.LibraryPage = LibraryPage;
